import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from 'react';
import { Sort } from "@models";
import { MenuItemExample } from "@apiHooks/useMenuExamplesPopupData";

const BYO_MENU_EXAMPLES_ENDPOINT: keyof paths = '/ByoMenuExamples';
const BYO_MENU_EXAMPLES_EXPORT_ENDPOINT: keyof paths = '/ByoMenuExampleExport';

type ByoMenuExamplesRequest = components['schemas']['Application.Byo.Models.ByoMenuExamplesFilterInput'];
type ByoMenuExamplesResponse = components['schemas']['Application.Byo.Models.MenuExamplesOutputModel'];
type MainFilterInput = components['schemas']['Application.Byo.Models.ByoMenuExamplesFilterInput.ByoMenuExampleFilterInput'];
type TableParameters = components["schemas"]["Application.Byo.Models.ByoMenuExamplesFilterInput.TParameters"];
export type ByoMenuExamplesExportRequest = components['schemas']['Application.Byo.Export.ByoMenuExampleExportFilter'];
export type SliceParameter = components['schemas']['Application.Byo.Models.ByoMenuExamplesFilterInput.Slice'];

type FetchOptions = {
    sortColumn: number;
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

export interface UseByoMenuExamplesParams {
    word: MainFilterInput['word'] | null;
    filters: MainFilterInput['filters'];
    fullFilter: ByoMenuExamplesExportRequest['fullFilter'];
    sliceParameter: SliceParameter | null;
}

export default function useByoMenuExamples({
    word,
    filters,
    fullFilter,
    sliceParameter,
}: UseByoMenuExamplesParams) {
    const {
        doFetch,
        cancel: cancelFetch,
    } = useAxios<ByoMenuExamplesResponse, ByoMenuExamplesRequest>({
        method: 'post',
        url: BYO_MENU_EXAMPLES_ENDPOINT,
        lazy: true,
    });

    const {
        doFetch: doExport,
        cancel: cancelExport,
    } = useAxios<string, ByoMenuExamplesExportRequest>({
        method: 'post',
        url: BYO_MENU_EXAMPLES_EXPORT_ENDPOINT,
        lazy: true,
    });

    const fetch = useCallback(
        async (options: FetchOptions): Promise<FetchResponse> => {
            if (!word) {
                return { data: [], totalRecords: 0 };
            }

            const tableParameters: TableParameters = {
                sortColumn: options.sortColumn as TableParameters['sortColumn'],
                search: options.search,
                take: options.take,
                skip: options.skip,
                sortDir: options.sortDir === Sort.Asc ? 0 : 1,
            };

            const requestData: ByoMenuExamplesRequest = {
                mainFilter: {
                    word,
                    filters,
                },
                sliceParameter: sliceParameter ?? undefined,
                tableParameters,
            };

            const response = await doFetch({ data: requestData });

            return {
                data: response?.data?.data ?? [],
                totalRecords: response?.data?.totalRecords ?? 0,
            };
        },
        [word, filters, sliceParameter, doFetch]
    );

    const fetchExport = useCallback(
        async (options: FetchOptions): Promise<string | void> => {
            if (!word) return;

            const tableParameters: TableParameters = {
                sortColumn: options.sortColumn as TableParameters['sortColumn'],
                search: options.search,
                take: options.take,
                skip: options.skip,
                sortDir: options.sortDir === Sort.Asc ? 0 : 1,
            };

            const requestData: ByoMenuExamplesExportRequest = {
                fullFilter,
                customerFilter: {
                    mainFilter: {
                        word,
                        filters,
                    },
                    sliceParameter: sliceParameter ?? undefined,
                    tableParameters,
                },
            };

            const response = await doExport({ data: requestData });
            return response?.data;
        },
        [word, filters, fullFilter, sliceParameter, doExport]
    );

    const cancel = useCallback(() => {
        cancelFetch();
        cancelExport();
    }, [cancelFetch, cancelExport]);

    const isReady = useMemo(() => !!word, [word]);

    return {
        fetch,
        fetchExport,
        cancel,
        isReady,
        downloadUrl: BYO_MENU_EXAMPLES_EXPORT_ENDPOINT,
    };
}
